import { io, type Socket } from "socket.io-client";
import { useAuthStore } from "@/store/auth.store";

/**
 * Centralized Socket.IO client — a single shared connection for the whole app.
 * Components/hooks subscribe to events via useSocketEvent(); nothing should call
 * io() a second time (see admin build rules, section 17: no duplicate connections).
 *
 * Matches apps/location-service/src/realtime.gateway.ts: handshake auth carries
 * the access JWT (`{ auth: { token } }`), and an admin-role JWT is auto-joined
 * to the `admins` room on connect. We also emit ADMIN_SUBSCRIBE explicitly as
 * a defensive re-join (e.g. after a gateway restart) — its ack includes a
 * fresh driver-location snapshot, mirrored here into the location-service
 * REST endpoint we already call for the initial map load.
 */
let socket: Socket | null = null;

export function getSocket(): Socket {
  if (socket) return socket;

  const url = import.meta.env.VITE_SOCKET_URL ?? "http://localhost:3000";
  const token = useAuthStore.getState().accessToken;

  socket = io(url, {
    path: "/socket.io",
    auth: { token },
    transports: ["websocket"],
    autoConnect: false,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10_000,
  });

  socket.on("connect", () => {
    socket?.emit(SocketEvents.AdminSubscribe);
  });

  return socket;
}

export function connectSocket(): Socket {
  const s = getSocket();
  if (!s.connected) {
    // Refresh auth payload in case the token rotated since the socket was created.
    s.auth = { token: useAuthStore.getState().accessToken };
    s.connect();
  }
  return s;
}

export function disconnectSocket() {
  socket?.disconnect();
}

/**
 * Real event names from packages/common/src/constants/realtime.ts SocketEvents.
 * Only the ones relevant to the admin dashboard are listed here.
 */
export const SocketEvents = {
  AdminSubscribe: "admin:subscribe",
  AdminDriverLocation: "admin:driver:location",
  AdminRideUpdate: "admin:ride:update",
  AdminSupportMessage: "admin:support:message",
  DriverStatus: "driver:status",
  Notification: "notification",
} as const;
